import useAppMutation from 'hooks/useAppMutation';
import useNotify from 'hooks/useNotify';
import { useTranslation } from 'react-i18next';
import { getErrorMessage } from 'utils/axiosExceptionUtils';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { DESCRIPTIONS_BASE_QUERY_KEY } from 'components/domain/property/useFetchDescriptions';
import useUpdateDescriptionMutation from 'components/domain/property/useUpdateDescriptionMutation';
import { FormContextParts } from 'components/forms/form/Form.types';
import { Property, PropertyBusinessType } from 'models/Properties';
import { TFunction } from 'i18next';
import { Description } from 'models/Descriptions';
import {
  PropertySettingsDescriptionsTabFormValues,
  getFormDefaultValues,
} from './PropertySettingsDescriptionsTab.schema';

const handleUpdateSuccess = async ({
  descriptionFields,
  notifySuccess,
  queryClient,
  propertyUid,
  currentLanguage,
  reset,
  t,
}: {
  descriptionFields: PropertySettingsDescriptionsTabFormValues;
  propertyUid: string;
  queryClient: QueryClient;
  currentLanguage: string;
  reset: (values: PropertySettingsDescriptionsTabFormValues) => void;
  notifySuccess: (message: string) => void;
  t: TFunction;
}) => {
  const query = [DESCRIPTIONS_BASE_QUERY_KEY, 'list', propertyUid];

  const cachedDescriptions = queryClient.getQueryData<Description[]>(query);

  const updatedDescriptions = cachedDescriptions?.map((description) => {
    if (description.locale === currentLanguage) {
      return descriptionFields;
    }

    return description;
  });

  queryClient.setQueryData(query, updatedDescriptions);

  const updatedFormValues = updatedDescriptions?.find(
    ({ locale }) => locale === currentLanguage,
  );

  if (updatedFormValues) {
    reset(getFormDefaultValues(updatedFormValues));
  }

  notifySuccess(t('pageProperty.descriptions.success'));
};

const useUpdateDescription = ({
  businessType,
  currentLanguage,
  propertyUid,
}: {
  businessType?: PropertyBusinessType;
  currentLanguage: string;
  propertyUid: Property['uid'];
}) => {
  const { notifyError, notifySuccess } = useNotify();
  const { t } = useTranslation();
  const { mutateAsync: doUpdateDescription } = useUpdateDescriptionMutation();
  const queryClient = useQueryClient();

  const { mutateAsync: updateDescription, isLoading: isUpdating } =
    useAppMutation(
      ({
        descriptionFields,
      }: {
        descriptionFields: PropertySettingsDescriptionsTabFormValues;
        reset: (values: PropertySettingsDescriptionsTabFormValues) => void;
      }) => {
        return doUpdateDescription({
          isHotel: businessType === PropertyBusinessType.HOTEL,
          propertyUid,
          locale: currentLanguage,
          ...descriptionFields,
        });
      },
      {
        onSuccess: (_, { reset, descriptionFields }) => {
          handleUpdateSuccess({
            descriptionFields,
            notifySuccess,
            queryClient,
            propertyUid,
            currentLanguage,
            reset,
            t,
          });
        },
        onError: (error: any) => {
          notifyError(
            getErrorMessage({
              apiErrorCode: error.response.data?.apiErrorCode,
              apiErrorMessage: error.response.data?.apiErrorMessage,
              baseKey: 'pageProperty.apiErrorCodes.',
              t,
            }),
          );
        },
      },
    );

  const handleUpdateDescriptionSubmit = async (
    {
      isApplyADifferentLongDescriptionEnabled,
      ...values
    }: PropertySettingsDescriptionsTabFormValues,
    // @ts-expect-error TS7006 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    _,
    { reset }: FormContextParts<PropertySettingsDescriptionsTabFormValues>,
  ) => {
    const descriptionFields = { ...values };

    // Clears bookingDotComSummary if isApplyADifferentLongDescriptionEnabled is false, so that the checkbox does not
    // get enabled next time the user visits the page
    if (
      !isApplyADifferentLongDescriptionEnabled &&
      descriptionFields.bookingDotComSummary?.length
    ) {
      descriptionFields.bookingDotComSummary = '';
    }

    await updateDescription({
      descriptionFields,
      reset,
    });
  };

  return {
    handleUpdateDescriptionSubmit,
    isUpdating,
  };
};

export default useUpdateDescription;
